import React from 'react'
import { Private } from '@ally/private'
import {
  Alert,
  Box,
  ScreenReaderOnly,
  Table,
  TableColumn,
  TextBody,
  get,
  useMediaSize,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import { AccountDetails, IraPlan } from '../../types'
import {
  getAllocationPercentage,
  getBeneficiaryDisplayName,
  formatDOB,
} from '../../utils/formatter'
import { LastRowBox } from './LastRowBox'

import { DeleteBeneficiaryModal } from '../NonIraBeneficiaries/DeleteBeneficiaryFlow/DeleteBeneficiaryModal'
import { EditBeneficiaryModal } from '../NonIraBeneficiaries/EditBeneficiaryFlow/EditBeneficiaryModal'
import { MAX_IRA_BENES, MAX_NON_IRA_BENES } from '../../utils/constants'
import { AddBeneficiaryModal } from './AddBeneficiary/AddBeneficiaryModal'
import { NonPrintingWrapper } from '../../utils/NonPrintingWrapper'

type TableData = {
  beneficiaryId: string
  beneficiariesName: string
  dateOfBirth: string
  type?: string
  allocation: string
}

// The need for !important in the mobile styling is due to the metronome mobile table overwriting the display
const StyledActionIcons = styled(Box)`
  display: flex;
  gap: 15%;
  justify-content: right;
  align-items: center;
  ${get.media.down('md')} {
    display: inline !important;
  }
`

const StyledTable = styled(Table)`
  ${get.media.down('md')} {
    li > dl > div {
      flex: 0 33% !important;
    }
  }
`

const StyledNoBeneficiariesRow = styled(Box)`
  display: none;
  @media print {
    display: block;
  }
`

export const BeneficiariesTable: React.FC<{
  account: AccountDetails | IraPlan
  isIraPage: boolean
  accountLabel: string
}> = ({ account, isIraPage, accountLabel }) => {
  const isMdDown = useMediaSize('MdDown')
  const isPrint = window.matchMedia('print').matches
  const maxNum = isIraPage ? MAX_IRA_BENES : MAX_NON_IRA_BENES
  const noBeneficiaries = (account.beneficiaries?.length || 0) === 0
  const maxBeneficiaries = (account.beneficiaries?.length || 0) >= maxNum

  const tableData = (): TableData[] => {
    if (account.beneficiaries && account.beneficiaries.length > 0) {
      const data: TableData[] = account.beneficiaries.reduce(
        (accumulator, bene) => {
          accumulator.push({
            beneficiaryId: bene.beneficiaryId,
            beneficiariesName: getBeneficiaryDisplayName(bene),
            dateOfBirth: formatDOB(bene.dateOfBirth),
            type: bene.beneficiaryType || '',
            allocation: getAllocationPercentage(
              bene.allocationPercentage || '',
            ),
          })
          return accumulator
        },
        [] as TableData[],
      )
      return data
    }
    return []
  }

  // This sort function sorts the ira table data in reverse alphabetical order
  // based on the beneficiary type. This ensures the table renders with the
  // Primary benes first followed by the Contingent benes.
  const sortIraTableData = (a: TableData, b: TableData): number =>
    (a.type || '') > (b.type || '') ? -1 : 1

  const nonIraColumns: TableColumn<TableData>[] = [
    {
      Header: 'Beneficiaries',
      accessor: 'beneficiariesName',
      alignment: 'left',
      Cell: ({
        cell: { value },
      }: {
        cell: { value: any }
      }): React.ReactElement => {
        return <Private>{value}</Private>
      },
    },
    {
      Header: 'Date of Birth',
      accessor: 'dateOfBirth',
      alignment: 'left',
      Cell: ({
        cell: { value },
      }: {
        cell: { value: any }
      }): React.ReactElement => {
        return value ? (
          <Private>{formatDOB(value)}</Private>
        ) : (
          <span aria-label="Date of birth not applicable">-/-</span>
        )
      },
    },
    {
      Header: 'Allocation',
      accessor: 'allocation',
      alignment: 'left',
    },
    {
      Header: <ScreenReaderOnly>Actions</ScreenReaderOnly>,
      accessor: 'actions',
      alignment: 'right',
      Cell: ({
        cell: {
          row: {
            original: { beneficiaryId },
          },
        },
      }: {
        cell: { row: { original: TableData } }
      }): React.ReactElement => {
        return (
          <NonPrintingWrapper>
            <StyledActionIcons>
              <EditBeneficiaryModal
                beneficiaries={account.beneficiaries}
                beneficiaryId={beneficiaryId}
                accountId={account.accountId}
                accountLabel={accountLabel}
                isIraPage={isIraPage}
              />
              <DeleteBeneficiaryModal
                beneficiaryId={beneficiaryId}
                accountId={account.accountId} // account id
                beneficiaries={
                  // istanbul ignore next - low value, already tested option chaining
                  account.beneficiaries
                }
                isIraPage={isIraPage}
                accountLabel={accountLabel}
              />
            </StyledActionIcons>
          </NonPrintingWrapper>
        )
      },
    },
  ]

  const iraColumns: TableColumn<TableData>[] = [
    {
      Header: 'Beneficiaries',
      accessor: 'beneficiariesName',
      alignment: 'left',
      Cell: ({
        cell: { value },
      }: {
        cell: { value: any }
      }): React.ReactElement => {
        return <Private>{value}</Private>
      },
    },
    {
      Header: 'Date of Birth',
      accessor: 'dateOfBirth',
      alignment: 'left',
      Cell: ({
        cell: { value },
      }: {
        cell: { value: any }
      }): React.ReactElement => {
        return value ? (
          <Private>{formatDOB(value)}</Private>
        ) : (
          <span aria-label="Date of birth not applicable">-/-</span>
        )
      },
    },
    {
      Header: 'Type',
      accessor: 'type',
      alignment: 'left',
    },
    {
      Header: 'Allocation',
      accessor: 'allocation',
      alignment: 'left',
    },
    {
      Header: <ScreenReaderOnly>Actions</ScreenReaderOnly>,
      accessor: 'actions',
      alignment: 'right',
      Cell: ({
        cell: {
          row: {
            original: { beneficiaryId },
          },
        },
      }: {
        cell: { row: { original: TableData } }
      }): React.ReactElement => {
        return (
          <NonPrintingWrapper>
            <StyledActionIcons>
              <EditBeneficiaryModal
                beneficiaries={account.beneficiaries}
                beneficiaryId={beneficiaryId}
                accountId={account.planId ?? ''}
                accountLabel={accountLabel}
                isIraPage
              />
              <DeleteBeneficiaryModal
                beneficiaryId={beneficiaryId}
                accountId={account.planId ?? ''}
                accountLabel={accountLabel}
                beneficiaries={account.beneficiaries}
                isIraPage={isIraPage}
              />
            </StyledActionIcons>
          </NonPrintingWrapper>
        )
      },
    },
  ]

  const lastRow = (): React.ReactElement | undefined => {
    // HT and L is ineligible ownershipType and need to hide add bene flow and show message
    // HT: Uniform Transfer to Minors
    // L: Representative Payee
    if (
      !isIraPage &&
      (account.ownershipType === 'HT' || account.ownershipType === 'L')
    ) {
      return (
        <NonPrintingWrapper>
          <LastRowBox alignment="left">
            <TextBody size="sm" tag="p">
              You can&apos;t add beneficiaries to this account.
            </TextBody>
          </LastRowBox>
        </NonPrintingWrapper>
      )
    }
    // istanbul ignore next - print only
    if (isPrint && noBeneficiaries) {
      return (
        <StyledNoBeneficiariesRow ml="md">
          No beneficiaries linked to this account
        </StyledNoBeneficiariesRow>
      )
    }
    if (maxBeneficiaries) {
      return (
        <NonPrintingWrapper>
          <LastRowBox
            alignment="left"
            accountId={isIraPage ? account.planId : account.accountId}
          >
            <Alert
              variant="warning"
              body={`You can only have ${maxNum} beneficiaries. You'll need to delete a current beneficiary if you want to add another one.`}
              disableContainer
            />
          </LastRowBox>
        </NonPrintingWrapper>
      )
    }
    if (!isMdDown) {
      return (
        <NonPrintingWrapper>
          <LastRowBox alignment="left">
            <AddBeneficiaryModal
              account={account}
              isIraPage={isIraPage}
              accountLabel={accountLabel}
            />
          </LastRowBox>
        </NonPrintingWrapper>
      )
    }
    return <></>
  }
  // HT and L is ineligible ownershipType and need to hide add bene flow
  // HT: Uniform Transfer to Minors
  // L: Representative Payee
  return (
    <>
      {isMdDown &&
      !maxBeneficiaries &&
      account.ownershipType !== 'HT' &&
      account.ownershipType !== 'L' ? (
        <NonPrintingWrapper>
          <AddBeneficiaryModal
            account={account}
            isIraPage={isIraPage}
            accountLabel={accountLabel}
          />
        </NonPrintingWrapper>
      ) : (
        <></>
      )}
      <StyledTable
        config={{
          data: isIraPage ? tableData().sort(sortIraTableData) : tableData(),
          columns: isIraPage ? iraColumns : nonIraColumns,
        }}
        shared={{
          captionText: isIraPage
            ? 'IRA Beneficiaries Table'
            : 'Non-IRA Beneficiaries Table',
          disableContainer: true,
          displayCardList: isPrint
            ? /* istanbul ignore next - print only */ 'none'
            : 'md-down',
        }}
        table={{ fullWidth: true }}
        cardList={{ caption: { headingLevel: 'h5' }, colCount: '2' }}
      />
      {lastRow()}
    </>
  )
}
